import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useChat } from "@ai-sdk/react";
import {
  DefaultChatTransport,
  lastAssistantMessageIsCompleteWithApprovalResponses,
  type UIMessage,
} from "ai";
import { useAuth } from "@/contexts/AuthContext";

const CHAT_ENDPOINT = "/api/agent/chat";
const CONVERSATION_ENDPOINT = "/api/agent/conversation";

/**
 * Conversation ids are keyed per user AND per project.
 *
 * Per project so switching clients does not drag one client's thread along.
 * Per user because on a shared browser the next person to sign in would
 * otherwise inherit an id whose row belongs to someone else, and every send
 * would 403 forever with no way for them to know why.
 */
function conversationStorageKey(userKey: string, scopeKey?: string): string {
  return `advisor_assistant_conversation_${userKey}_${scopeKey ?? "global"}`;
}

/** `crypto.randomUUID` is secure-context only, so it is absent over plain http
 *  (e.g. `vite --host` from a phone). Falling back beats crashing the page. */
function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** localStorage throws outright when storage is blocked (Safari, "block all
 *  cookies"), so every access degrades to an in-memory id instead. */
const memoryStore = new Map<string, string>();

function readOrCreateConversationId(userKey: string, scopeKey?: string): string {
  const key = conversationStorageKey(userKey, scopeKey);
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const created = newId();
    localStorage.setItem(key, created);
    return created;
  } catch {
    const existing = memoryStore.get(key);
    if (existing) return existing;
    const created = newId();
    memoryStore.set(key, created);
    return created;
  }
}

function persistConversationId(userKey: string, scopeKey: string | undefined, id: string) {
  const key = conversationStorageKey(userKey, scopeKey);
  try {
    localStorage.setItem(key, id);
  } catch {
    memoryStore.set(key, id);
  }
}

export interface UseAssistantChatOptions {
  /** Client the assistant should scope its tools to. */
  projectId?: string;
  /** Marketplace campaign the user has open, if any. */
  campaignId?: string;
}

export function useAssistantChat({ projectId, campaignId }: UseAssistantChatOptions = {}) {
  // One stored thread per scope. Campaign wins over project: they never apply
  // at once, and a campaign is the narrower context.
  const scopeKey = campaignId ?? projectId;
  const { getIdToken, firebaseUser } = useAuth();
  const location = useLocation();
  const userKey = firebaseUser?.uid ?? "anon";

  const [conversationId, setConversationId] = useState(() =>
    readOrCreateConversationId(userKey, scopeKey),
  );
  const [isHydrating, setIsHydrating] = useState(true);

  // `getIdToken` is a fresh closure on every AuthProvider render, so depending on
  // it directly would re-run the hydration effect (and re-fetch the thread) on
  // every auth-context render. Hold it in a ref and depend only on the id.
  const getIdTokenRef = useRef(getIdToken);
  getIdTokenRef.current = getIdToken;

  // Written in an effect, not during render: react-router wraps navigations in
  // startTransition, and a render React discards would otherwise leave the ref
  // pointing at a project the user never actually landed on.
  const requestScope = useRef({ projectId, campaignId, route: location.pathname });
  useEffect(() => {
    requestScope.current = { projectId, campaignId, route: location.pathname };
  }, [projectId, campaignId, location.pathname]);

  const transport = useMemo(
    () =>
      new DefaultChatTransport<UIMessage>({
        api: CHAT_ENDPOINT,
        // Resolved per request: Firebase ID tokens expire hourly and
        // `getIdToken` refreshes them transparently, which is what replaces the
        // manual 401-retry loop the original client needed.
        headers: async (): Promise<Record<string, string>> => {
          const token = await getIdTokenRef.current();
          return token ? { Authorization: `Bearer ${token}` } : {};
        },
        body: () => ({
          projectId: requestScope.current.projectId,
          campaignId: requestScope.current.campaignId,
          route: requestScope.current.route,
          timezoneOffsetMinutes: -new Date().getTimezoneOffset(),
        }),
      }),
    [],
  );

  const chat = useChat<UIMessage>({
    id: conversationId,
    transport,
    // Once every pending approval is answered, resubmit automatically so the
    // agent resumes where it paused instead of waiting for another message.
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
    // Streaming pushes a new messages array per chunk; without this the sidebar
    // re-renders (and re-runs its scroll effect) dozens of times a second.
    throttle: 50,
  });

  const { setMessages, messages, status, stop } = chat;

  // Live view of chat state for the hydration guard below, without making the
  // effect depend on values that change on every streamed chunk.
  const liveRef = useRef({ messages, status });
  liveRef.current = { messages, status };

  // Rehydrate the stored thread whenever the conversation changes.
  useEffect(() => {
    let cancelled = false;
    setIsHydrating(true);

    (async () => {
      try {
        const token = await getIdTokenRef.current();
        if (!token) return;

        const response = await fetch(
          `${CONVERSATION_ENDPOINT}?id=${encodeURIComponent(conversationId)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!response.ok) return;

        const data = (await response.json()) as { messages?: UIMessage[] };
        if (cancelled || !data.messages?.length) return;

        // Only adopt the stored thread if nothing has happened locally since the
        // fetch began. A slow hydration that lands mid-turn would otherwise
        // replace the in-flight exchange with a stale snapshot — and because the
        // server rewrites the whole thread from what the client posts, that
        // corrupted array would then be written back over the real history.
        const live = liveRef.current;
        if (live.messages.length > 0 || live.status !== "ready") return;

        setMessages(data.messages);
      } catch {
        // A failed rehydrate just starts from an empty thread. Never block chat.
      } finally {
        if (!cancelled) setIsHydrating(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [conversationId, setMessages]);

  // Switching project mid-stream: abort first so the old request stops burning
  // a 300s function and Anthropic tokens for an answer nobody will see.
  useEffect(() => {
    const next = readOrCreateConversationId(userKey, scopeKey);
    setConversationId((current) => {
      if (current === next) return current;
      stop();
      return next;
    });
  }, [scopeKey, userKey, stop]);

  /** Abandon the current thread and start a fresh one for this project. */
  const startNewConversation = useCallback(() => {
    stop();
    const created = newId();
    persistConversationId(userKey, scopeKey, created);
    setMessages([]);
    setConversationId(created);
  }, [scopeKey, userKey, setMessages, stop]);

  /**
   * True while an approval card is waiting on the user. Sending a new message in
   * that state posts a tool call with no result, which Anthropic rejects — and
   * because the unanswered part stays in client state, every retry reproduces
   * the same failure and the thread is permanently stuck.
   */
  const hasPendingApproval = useMemo(() => {
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant") return false;
    return last.parts.some(
      (part) =>
        part.type.startsWith("tool-") &&
        (part as { state?: string }).state === "approval-requested",
    );
  }, [messages]);

  return { ...chat, conversationId, isHydrating, hasPendingApproval, startNewConversation };
}
